/* UploadUpgradeResultsVeilRenderer.js */

"use strict";

/**
 * UploadUpgradeResultsVeilRenderer
 *
 * Renders the UI veil for uploading offline device upgrade result files.
 * Provides controls for file selection (via drag & drop or file dialog),
 * a cancellation button, and manages event listeners for the import process.
 *
 * @class
 * @param {Object} model - The view model backing this renderer. Must provide logger and importFile/cancelAction methods.
 */
const InputFileControl = require("@gluenetworks/steve_ui_core").Control.Action.InputFile;
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;

class UploadUpgradeResultsVeilRenderer {
    /**
     * @param {Object} model The model object for this renderer.
     */
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("import-screen-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;

        this._importScreen = document.createElement("div");
        this._importScreen.setAttribute("type", "text");
        this._importScreen.setAttribute("draggable", true);

        this._titleText = document.createElement("h3");
        this._titleText.innerHTML = "Drop Upgrade Result Files<br/>to Import";

        this._buttonBarElem = document.createElement("div");
        this._buttonBarElem.classList.add("button-bar");

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
        });

        this._importFromFile = new InputFileControl({
            id: "iotExplorereFileUploadControl",
            label: "Import Result Files",
            acceptFiles: ".zip, .gz",
            darkTheme: true,
            multiple: true,
        });

        this._importFromFile.setAttribute("style", "margin-right:15px;");

        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    /**
     * Register all event listeners for UI controls and drag/drop.
     * @private
     */
    _registerListeners() {
        this._importScreen.addEventListener(
            "drop",
            (this._onDropFromFile = this._onDropFromFile.bind(this))
        );
        this._importScreen.addEventListener(
            "dragover",
            (this._onDragover = this._onDragover.bind(this))
        );
        this._importFromFile.on(
            "FileHandle",
            (this._onImportFromFile = this._onImportFromFile.bind(this))
        );
        this._cancelButton.on(
            "UserClick",
            (this._onCancelButtonClick = this._onCancelButtonClick.bind(this))
        );
    }

    /**
     * Unregister all event listeners for cleanup.
     * @private
     */
    _unregisterListeners() {
        this._importScreen.removeEventListener("drop", this._onDropFromFile);
        this._importScreen.removeEventListener("dragover", this._onDragover);
        this._importFromFile.removeListener("FileHandle", this._onImportFromFile);
        this._cancelButton.removeListener("UserClick", this._onCancelButtonClick);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Tears down the renderer, unregistering listeners and removing DOM nodes.
     * @returns {Promise} Resolves when all controls shut down and DOM nodes are cleaned up.
     */
    shutdown() {
        this._unregisterListeners();

        let promArr = [this._cancelButton.shutdown(), this._importFromFile.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._importFromFile = undefined;
                this._cancelButton = undefined;
                this._titleText.remove();
                this._titleText = undefined;
                this._importScreen.remove();
                this._importScreen = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "UploadUpgradeResultsVeilRenderer.shutdown() error."
                );
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve /*,reject*/) => {
            if (!this._rootElem.hasChildNodes()) {
                this._rootElem.appendChild(this._importScreen);
                this._importScreen.appendChild(this._titleText);
                this._rootElem.appendChild(this._buttonBarElem);
                this._buttonBarElem.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonBarElem.appendChild(this._importFromFile.renderer.rootElement);
            }
            resolve();
        });
    }

    /**
     * The root element for this renderer.
     * @type {HTMLElement}
     * @readonly
     */
    get rootElement() {
        return this.getRootElement();
    }

    /**
     * Returns the root element for this renderer.
     * @returns {HTMLElement}
     */
    getRootElement() {
        return this._rootElem;
    }

    /**
     * The backing model for this renderer.
     * @type {Object}
     * @readonly
     */
    get model() {
        return this.getModel();
    }

    /**
     * Returns the backing model for this renderer.
     * @returns {Object}
     */
    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /**
     * Handler for cancel button click. Cancels the import action.
     * @private
     */
    _onCancelButtonClick() {
        let err = new Error("user rejected");
        err.aborted = true;
        this.model.cancelAction(err);
    }

    /**
     * Handles file drop or file dialog selection.
     * Passes the selected files to the model's importFile method.
     * @param {Event} event - Drop or FileHandle event.
     * @private
     */
    _onImportFromFile(event) {
        event.preventDefault();
        this._model.importFile(event.target.files);
    }

    /**
     * Handles file drop event (unused, left for possible extension).
     * @param {Event} event
     * @private
     */
    _onDropFromFile(event) {
        event.preventDefault();
        this._model.importFile(event.dataTransfer.files);
    }

    /**
     * Handler for dragover event to enable drop.
     * @param {Event} event
     * @private
     */
    _onDragover(event) {
        event.preventDefault();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = UploadUpgradeResultsVeilRenderer;
